import React, { useState, useEffect, useRef, useCallback } from 'react'
import axios from 'axios'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import Messages from './Messages'
import BodyMap from './BodyMap'
import { useCaleb } from '../context/CalebContext'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

// Compute age from a DOB string (YYYY-MM-DD). Falls back to stored age if no DOB.
function calcAge(dob, storedAge) {
  if (!dob) return storedAge || null
  const today = new Date()
  const birth  = new Date(dob)
  if (isNaN(birth.getTime())) return storedAge || null
  let age = today.getFullYear() - birth.getFullYear()
  const beforeBirthday =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  if (beforeBirthday) age--
  return age
}

const TEAL        = '#0d9488'
const TEAL_DARK   = '#0f766e'
const TEAL_MID    = '#99f6e4'
const TEAL_LIGHT  = '#f0fdfa'
const PURPLE      = '#7c3aed'
const PURPLE_DARK = '#6d28d9'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const DISPLAY    = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const EXERCISE_LIBRARY = {
  'Knee / ACL': [
    'Quad Sets', 'Short Arc Quads', 'Straight Leg Raises', 'Heel Slides', 'Ankle Pumps',
    'Mini Squats', 'Wall Squats', 'Step-Ups', 'Step-Downs', 'Leg Press', 'Hamstring Curls',
    'Terminal Knee Extensions', 'Single-Leg Press', 'Lateral Band Walks',
    'Hip Abduction (Side-Lying)', 'Stationary Bike', 'Balance Board',
  ],
  'Shoulder / Rotator Cuff': [
    'Pendulum Swings', 'Cross-Body Stretch', 'Doorway Stretch', 'GIRD Stretch',
    'Scapular Retractions', 'Wall Slides', 'Wall Angels', 'Shoulder Shrugs',
    'Side-Lying External Rotation', 'Internal Rotation (Band)', 'External Rotation (Band)',
    'Shoulder Flexion (Band)', 'Shoulder Abduction (Band)', 'Rows (Band)',
    'Lateral Raises', 'Front Raises',
  ],
  'Lower Back': [
    'Pelvic Tilts', 'Cat-Cow', 'Single Knee-to-Chest', 'Double Knee-to-Chest',
    'Lumbar Rotation Stretch', 'Quadruped Rocking', 'Press-Up (McKenzie)', "Child's Pose",
    'Bridges', 'Bird-Dog', 'Dead Bug', 'Prone Hip Extension', 'Side-Lying Hip Abduction',
    'Plank', 'Hip Flexor Stretch', 'Piriformis Stretch', 'Hamstring Stretch', 'Seated Forward Bend',
  ],
  'Hip': [
    'Hip Flexor Stretch', 'Figure-Four Stretch', 'Butterfly Stretch', 'Hip Circles', 'Pigeon Stretch',
    'Glute Bridges', 'Single-Leg Glute Bridges', 'Clamshells', 'Hip Abduction (Standing)',
    'Hip Extension (Standing)', 'Side-Lying Hip Adduction', 'Adductor Squeeze',
    'Lateral Band Walks', 'Step-Ups', 'Reverse Lunges', 'Walking Lunges', 'Single-Leg RDLs',
  ],
  'Ankle / Foot': [
    'Ankle Pumps', 'Ankle Circles', 'Alphabet Ankle', 'Achilles Stretch', 'Dorsiflexion Stretch',
    'Plantar Fascia Stretch', 'Calf Stretch (Straight Knee)', 'Calf Stretch (Bent Knee)',
    'Calf Raises', 'Single-Leg Calf Raises', 'Heel Walks', 'Toe Walks', 'Towel Scrunches',
    'Marble Pickups', 'Resistance Band Dorsiflexion', 'Resistance Band Plantarflexion',
    'Single-Leg Balance', 'BOSU Balance',
  ],
  'Neck / Cervical': [
    'Chin Tucks', 'Cervical Retraction', 'Cervical Rotation', 'Cervical Side Bending',
    'Neck Flexion / Extension', 'Upper Trap Stretch', 'Levator Scapulae Stretch', 'Scalene Stretch',
    'Isometric Neck Flexion', 'Isometric Neck Extension', 'Isometric Lateral Neck Resistance',
    'Scapular Retractions', 'Thoracic Extension', 'Shoulder Rolls', 'Doorway Pec Stretch',
  ],
  'Elbow / Wrist': [
    'Wrist Flexor Stretch', 'Wrist Extensor Stretch', 'Triceps Stretch',
    'Forearm Pronation / Supination', 'Wrist Flexion Curls', 'Wrist Extension Curls',
    'Wrist Radial Deviation', 'Wrist Ulnar Deviation', 'Eccentric Wrist Extensions',
    'Isometric Wrist Hold', 'Elbow Flexion / Extension', 'Theraband Twist',
    'Tennis Ball Squeezes', 'Finger Extensions (Band)', 'Putty Grip Strengthening',
  ],
  'Core / General': [
    'Plank', 'Side Plank', 'Dead Bug', 'Bird-Dog', 'Bridges', 'Pelvic Tilts',
    'Diaphragmatic Breathing', 'Sit-to-Stand', 'Marching in Place', 'Heel-Toe Walking',
    'Postural Retraining', 'Stationary Bike', 'Treadmill Walking', 'Pool Walking',
    'Elliptical', 'Foam Rolling', 'Balance Board',
  ],
}

const INJURY_TO_EXERCISE_GROUP = {
  'Lower Back Pain': 'Lower Back', 'Herniated Disc': 'Lower Back', 'Sciatica': 'Lower Back',
  'Neck Pain / Cervical Strain': 'Neck / Cervical', 'Scoliosis': 'Core / General',
  'Spinal Stenosis': 'Lower Back', 'Post-Spinal Surgery': 'Lower Back', 'Post-Spinal Fusion': 'Lower Back',
  'ACL Tear': 'Knee / ACL', 'Meniscus Tear': 'Knee / ACL', 'Patellofemoral Syndrome': 'Knee / ACL',
  'Knee Replacement': 'Knee / ACL', 'Patellar Tendinitis': 'Knee / ACL', 'IT Band Syndrome': 'Knee / ACL',
  'Post-ACL Reconstruction': 'Knee / ACL', 'Post-Knee Replacement': 'Knee / ACL',
  'Rotator Cuff Tear': 'Shoulder / Rotator Cuff', 'Shoulder Impingement': 'Shoulder / Rotator Cuff',
  'Frozen Shoulder': 'Shoulder / Rotator Cuff', 'Shoulder Replacement': 'Shoulder / Rotator Cuff',
  'Labrum Tear': 'Shoulder / Rotator Cuff', 'Shoulder Dislocation': 'Shoulder / Rotator Cuff',
  'Post-Shoulder Surgery': 'Shoulder / Rotator Cuff',
  'Hip Replacement': 'Hip', 'Hip Flexor Strain': 'Hip', 'Hip Labrum Tear': 'Hip',
  'Hip Bursitis': 'Hip', 'Piriformis Syndrome': 'Hip', 'Post-Hip Replacement': 'Hip',
  'Ankle Sprain': 'Ankle / Foot', 'Plantar Fasciitis': 'Ankle / Foot',
  'Achilles Tendinitis': 'Ankle / Foot', 'Stress Fracture': 'Ankle / Foot',
  'Tennis Elbow': 'Elbow / Wrist', "Golfer's Elbow": 'Elbow / Wrist',
  'Carpal Tunnel Syndrome': 'Elbow / Wrist', 'Wrist Fracture': 'Elbow / Wrist',
  'Osteoarthritis': 'Core / General', 'Osteoporosis': 'Core / General',
  'Fibromyalgia': 'Core / General', 'Vertigo / BPPV': 'Neck / Cervical',
  'Balance Disorder': 'Core / General', 'Concussion': 'Core / General',
  'Stroke Rehabilitation': 'Core / General', "Parkinson's Disease": 'Core / General',
  'Multiple Sclerosis': 'Core / General',
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Serif+Display:ital@0;1&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .tab-scroll::-webkit-scrollbar { display: none; }

  .pd-tab { transition: all .15s; }
  @keyframes micPulse { 0%,100% { box-shadow: 0 0 0 2px #fca5a5; } 50% { box-shadow: 0 0 0 5px rgba(239,68,68,0.2); } }
  .pd-tab:hover { border-bottom: 2px solid ${TEAL_MID} !important; color: ${TEAL} !important; }

  .pd-action-btn {
    background: rgba(255,255,255,0.13) !important;
    color: white !important;
    border: 1.5px solid rgba(255,255,255,0.28) !important;
    border-radius: 100px !important;
    transition: all .18s;
    cursor: pointer;
    font-family: ${FONT};
    font-size: 13px;
    font-weight: 600;
    padding: 8px 18px;
  }
  .pd-action-btn:hover {
    background: rgba(255,255,255,0.22) !important;
    transform: translateY(-1px);
  }
  .pd-action-btn.discharged-btn {
    border-color: rgba(74,222,128,0.45) !important;
    color: #4ade80 !important;
  }
  .pd-action-btn.discharged-btn:hover {
    background: rgba(74,222,128,0.15) !important;
  }

  .pd-card { transition: transform .2s; position: relative; overflow: hidden; }
  .pd-card::after { content: ''; position: absolute; inset: 0; border-radius: inherit; padding: 1.5px; background: linear-gradient(135deg, #0d9488, #7c3aed); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; z-index: 1; }
  .pd-card:hover { transform: translateY(-2px); }
  .pd-tab-content { background: rgba(255,255,255,0.92) !important; border: none !important; position: relative; }
  .pd-tab-content::after { content: ''; position: absolute; inset: 0; border-radius: 0 0 14px 14px; padding: 1.5px; background: linear-gradient(135deg, #0d9488, #7c3aed); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; }
  .pd-btn:hover { background: ${TEAL_DARK} !important; transform: translateY(-1px); }

  @keyframes blob1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(20px,-15px) scale(1.04)} 66%{transform:translate(-10px,10px) scale(0.97)} }
  @keyframes blob2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-20px,12px) scale(1.03)} 66%{transform:translate(15px,-20px) scale(0.96)} }

  /*  Responsive  */
  .pd-header { padding: 28px 32px; }
  .pd-tab { padding: 15px 22px; font-size: 14px; }
  .pd-grid-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  /* Large monitor (1440px+) — wider layouts */
  @media (min-width: 1440px) {
    .pd-header { padding: 36px 44px !important; }
    .pd-tab-content { padding: 36px 44px !important; }
    .pd-tab { padding: 17px 28px !important; font-size: 15px !important; }
    .pd-wide-2col { display: grid !important; grid-template-columns: 1fr 400px !important; gap: 40px !important; align-items: start !important; }
    .pd-form-grid { grid-template-columns: 1fr 1fr 1fr !important; }
    .pd-session-form { max-width: 700px !important; }
    .pd-compliance-strip { gap: 48px !important; }
  }
  @media (min-width: 1920px) {
    .pd-header { padding: 40px 56px !important; }
    .pd-tab-content { padding: 40px 56px !important; }
    .pd-wide-2col { grid-template-columns: 1fr 480px !important; gap: 56px !important; }
  }
  /* Mobile */
  @media (max-width: 640px) {
    .pd-header { padding: 20px 16px !important; }
    .pd-action-btn { font-size: 12px !important; padding: 6px 12px !important; }
    .pd-tab { padding: 12px 14px !important; font-size: 13px !important; }
    .pd-grid-2col { grid-template-columns: 1fr !important; }
    .pd-overview-grid { grid-template-columns: 1fr !important; }
    .pd-wide-2col { grid-template-columns: 1fr !important; }
    .pd-stat-name { font-size: 20px !important; }
    .pd-hide-mobile { display: none !important; }
    .pd-form-grid { grid-template-columns: 1fr 1fr !important; }
    .pd-card { margin-bottom: 12px !important; }
    .pd-tab-content { padding: 18px 14px !important; }
    .pd-compliance-strip { flex-direction: column !important; gap: 16px !important; }
  }
  @media (max-width: 960px) and (min-width: 641px) {
    .pd-action-btn { font-size: 12px !important; padding: 7px 14px !important; }
    .pd-form-grid { grid-template-columns: 1fr 1fr !important; }
  }
`

const labelStyle = {
  display: 'block', fontSize: '12px', fontWeight: '600',
  color: '#64748b', marginBottom: '5px',
  textTransform: 'uppercase', letterSpacing: '0.06em',
}

const inputStyle = {
  width: '100%', padding: '10px 13px', fontSize: '14px',
  border: '1.5px solid rgba(124,58,237,0.25)', borderRadius: '8px',
  boxSizing: 'border-box', color: '#1e293b', background: 'white',
  fontFamily: FONT, outline: 'none',
}

const sectionLabel = (text) => (
  <div style={{ fontSize: '11px', fontWeight: '700', color: TEAL, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '6px' }}>
    {text}
  </div>
)

//  Discharge modal 
const DISCHARGE_REASONS = [
  { value: 'goals_met',        label: 'Goals Met' },
  { value: 'patient_request',  label: 'Patient Request' },
  { value: 'non_compliance',   label: 'Non-Compliance' },
  { value: 'other',            label: 'Other' },
]

function DischargeModal({ patient, onConfirm, onCancel }) {
  const [reason, setReason] = useState('goals_met')
  const [notes, setNotes]   = useState('')
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', padding: '28px 32px',
        maxWidth: '440px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        fontFamily: FONT,
      }}>
        <div style={{ fontFamily: DISPLAY, fontSize: '20px', color: '#0f172a', marginBottom: '6px' }}>
          Discharge {patient.name}
        </div>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px', lineHeight: '1.6' }}>
          A discharge summary email will be sent to the patient if an email is on file.
        </p>

        <label style={labelStyle}>Discharge reason</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
          {DISCHARGE_REASONS.map(r => (
            <button key={r.value} onClick={() => setReason(r.value)} style={{
              padding: '6px 14px', borderRadius: '99px', fontSize: '13px', fontWeight: '600',
              fontFamily: FONT, cursor: 'pointer', transition: 'all .15s',
              background: reason === r.value ? TEAL : '#f1f5f9',
              color: reason === r.value ? 'white' : '#475569',
              border: `1.5px solid ${reason === r.value ? TEAL : 'rgba(124,58,237,0.25)'}`,
            }}>{r.label}</button>
          ))}
        </div>

        <label style={labelStyle}>Notes (optional)</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="e.g. Patient met all functional goals. Continue HEP independently."
          style={{ ...inputStyle, height: '90px', resize: 'vertical', marginBottom: '20px' }}
        />

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            padding: '10px 20px', border: '1.5px solid rgba(124,58,237,0.25)', borderRadius: '8px',
            background: 'white', color: '#475569', cursor: 'pointer', fontSize: '14px',
            fontWeight: '600', fontFamily: FONT,
          }}>Cancel</button>
          <button onClick={() => onConfirm({ reason, notes })} style={{
            padding: '10px 22px', background: TEAL, color: 'white', border: 'none',
            borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700',
            fontFamily: FONT,
          }}>Confirm Discharge</button>
        </div>
      </div>
    </div>
  )
}

//  Mic button 
function MicButton({ fieldName, activeField, onToggle, supported }) {
  if (!supported) return null
  const active = activeField === fieldName
  return (
    <button
      type="button"
      onClick={() => onToggle(fieldName)}
      title={active ? 'Stop recording' : 'Dictate with voice'}
      style={{
        padding: '5px 10px', border: 'none', borderRadius: '6px', cursor: 'pointer',
        background: active ? '#fef2f2' : '#f1f5f9',
        color:      active ? '#dc2626' : '#64748b',
        fontSize: '15px', lineHeight: 1, fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', gap: '5px',
        boxShadow: active ? '0 0 0 2px #fca5a5' : 'none',
        transition: 'all 0.15s',
        animation: active ? 'micPulse 1.2s ease-in-out infinite' : 'none',
      }}
    >
       {active ? <span style={{ fontSize: '11px', fontWeight: '700' }}>Recording…</span> : <span style={{ fontSize: '11px' }}>Dictate</span>}
    </button>
  )
}

function buildPrintableIntakeHTML(patientName, injuryType) {
  const line = (label, wide) => `
    <div class="field ${wide ? 'wide' : ''}">
      <div class="label">${label}</div>
      <div class="line"></div>
    </div>`
  const yesno = (label) => `
    <div class="field">
      <div class="label">${label}</div>
      <div class="yesno"> Yes &nbsp;&nbsp;  No</div>
    </div>`
  const scale = () => `
    <div class="field wide">
      <div class="label">Pain level (circle one)</div>
      <div class="scale">0 &nbsp;—&nbsp; 1 &nbsp;—&nbsp; 2 &nbsp;—&nbsp; 3 &nbsp;—&nbsp; 4 &nbsp;—&nbsp; 5 &nbsp;—&nbsp; 6 &nbsp;—&nbsp; 7 &nbsp;—&nbsp; 8 &nbsp;—&nbsp; 9 &nbsp;—&nbsp; 10</div>
      <div class="scale-labels"><span>No pain</span><span>Moderate</span><span>Worst imaginable</span></div>
    </div>`
  const section = (title, ...fields) => `
    <div class="section">
      <div class="section-title">${title}</div>
      <div class="grid">${fields.join('')}</div>
    </div>`

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <title>ClearPath PT — Intake Form</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; padding: 28px 32px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 2px solid #0d9488; padding-bottom: 12px; }
    .header h1 { font-size: 20px; color: #0d9488; font-weight: 700; }
    .header p { font-size: 11px; color: #555; margin-top: 3px; }
    .header-right { text-align: right; font-size: 11px; color: #555; }
    .section { margin-bottom: 16px; }
    .section-title { background: #f0fdfa; border-left: 3px solid #0d9488; padding: 5px 10px; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 10px; color: #0f766e; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 20px; }
    .field { display: flex; flex-direction: column; gap: 3px; }
    .field.wide { grid-column: span 2; }
    .label { font-size: 10px; font-weight: 700; color: #555; text-transform: uppercase; letter-spacing: 0.05em; }
    .line { border-bottom: 1px solid #888; height: 20px; }
    .yesno { font-size: 13px; padding-top: 4px; }
    .scale { font-size: 14px; font-weight: 700; letter-spacing: 2px; padding: 6px 0 2px; }
    .scale-labels { display: flex; justify-content: space-between; font-size: 9px; color: #777; }
    .pain-types { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; padding-top: 4px; }
    .pain-types span { font-size: 11px; }
    .consent-block { border: 1px solid #ccc; border-radius: 6px; padding: 10px 14px; font-size: 10px; color: #555; line-height: 1.6; margin-bottom: 10px; }
    .sig-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; }
    @media print { body { padding: 16px 20px; } }
  </style>
  </head><body>
  <div class="header">
    <div>
      <h1>ClearPath PT</h1>
      <p>Patient Intake Form</p>
      ${patientName ? `<p style="margin-top:6px;font-size:12px;"><strong>Patient:</strong> ${patientName}${injuryType ? ' &nbsp;|&nbsp; <strong>Injury:</strong> ' + injuryType : ''}</p>` : ''}
    </div>
    <div class="header-right">
      <div><strong>Date:</strong> ___________________</div>
      <div style="margin-top:6px;"><strong>Clinic:</strong> ___________________</div>
      <div style="margin-top:6px;"><strong>Therapist:</strong> ___________________</div>
    </div>
  </div>

  ${section('Personal Information',
    line('Full Name', true),
    line('Date of Birth'), line('Phone'),
    line('Address', true),
    line('Emergency Contact Name'), line('Emergency Contact Phone'),
    line('Referring Physician'), line('Primary Care Physician')
  )}

  ${section('Insurance & Billing',
    line('Insurance Provider'), line('Policy Number'),
    line('Group Number'), line('Policy Holder Name'),
    yesno("Workers' Compensation"), yesno('MVA / Auto Accident')
  )}

  ${section('Pain Assessment',
    scale(),
    `<div class="field wide"><div class="label">Pain type (circle all that apply)</div>
      <div class="pain-types">
        <span> Sharp</span><span> Dull / Aching</span><span> Burning</span><span> Throbbing</span>
        <span> Stabbing</span><span> Numbness / Tingling</span><span> Stiffness</span><span> Weakness</span>
      </div></div>`,
    line('Affected body region(s)', true),
    line('Mechanism of injury (how did it happen?)', true),
    line('When did symptoms start?'), line('Aggravating factors'),
    line('Relieving factors'), line('Functional limitations (what can you not do?)', true)
  )}

  ${section('Medical History',
    line('Current Medications', true),
    line('Allergies', true),
    line('Prior surgeries / procedures', true),
    yesno('Prior physical therapy?'), line('If yes, when and for what?'),
    `<div class="field wide"><div class="label">Conditions / Comorbidities (check all that apply)</div>
      <div class="pain-types" style="grid-template-columns:repeat(3,1fr);margin-top:4px;">
        <span> Diabetes</span><span> Hypertension</span><span> Heart Disease</span>
        <span> Osteoporosis</span><span> Arthritis</span><span> Depression / Anxiety</span>
        <span> COPD / Asthma</span><span> Neuropathy</span><span> Pacemaker / Implant</span>
        <span> Stroke / TIA</span><span> Cancer</span><span> Blood Clotting Disorder</span>
      </div></div>`
  )}

  ${section('Lifestyle & Goals',
    line('Occupation'), line('Work status'),
    line('Sports / hobbies / activities'), line('Activity level'),
    line('What are your goals for physical therapy?', true)
  )}

  <div class="section">
    <div class="section-title">Consent</div>
    <div class="consent-block">
      I authorize the physical therapy staff to perform an evaluation and treatment. I understand that physical therapy involves hands-on treatment and therapeutic exercise, and that I may experience temporary soreness. I consent to the collection and use of my health information for treatment purposes as described in the clinic's Notice of Privacy Practices, which has been made available to me.
    </div>
    <div class="sig-grid">
      ${line('Patient signature')}
      ${line('Date')}
    </div>
    <div style="margin-top:10px;">${line('Printed name')}</div>
  </div>
  </body></html>`
}

function PatientDashboard({ patient, onToast, userRole, featureFlags = {}, currentUser }) {
  // Default all features to true if no flags provided (trial / legacy clinics)
  const ff = {
    caleb: true, soap_ai: true, discharge: true, compliance: true,
    hep: true, pdf: true, plateau: true, outcomes: true,
    ...featureFlags,
  }
  const { open: openCaleb, isOpen: calebPanelOpen } = useCaleb()

  const [progress, setProgress]             = useState(null)
  const [goals, setGoals]                   = useState([])
  const [exercises, setExercises]           = useState([])
  const [exerciseLogs, setExerciseLogs]     = useState([])
  const [compliance, setCompliance]         = useState(null)
  const [unreadCount, setUnreadCount]       = useState(0)
  const [showDischargeModal, setShowDischargeModal] = useState(false)
  const [sessionForm, setSessionForm]       = useState({
    session_number: '', date: '', pain_score: '', rom: '',
    exercises_completed: '', notes: '', soap_subjective: '',
    soap_objective: '', soap_assessment: '', soap_plan: '',
  })
  const [sessionChecklist, setSessionChecklist] = useState({})
  const [goalForm, setGoalForm]             = useState({ goal_text: '', target_value: '', set_by: 'Therapist' })
  const [exerciseForm, setExerciseForm]     = useState({
    week_number: '', exercise_name: '', exercise_name_custom: '',
    sets: '', reps: '', notes: '', video_url: '',
  })
  const [activeTab, setActiveTab]           = useState('Overview')
  const [notesMode, setNotesMode]           = useState('simple')
  const [soapDrafting, setSoapDrafting]     = useState(false)
  // Referral tab state
  const [referralForm, setReferralForm]     = useState({
    referring_doctor: patient.referring_doctor || '',
    referring_doctor_email: patient.referring_doctor_email || '',
    referring_doctor_practice: patient.referring_doctor_practice || '',
  })
  const [referralSaving, setReferralSaving] = useState(false)
  const [thankYouSending, setThankYouSending] = useState(false)
  const [plateauInterventions, setPlateauInterventions] = useState(null)
  const [plateauLoading, setPlateauLoading] = useState(false)
  const [plateauError, setPlateauError] = useState(false)
  const [doctorEmailSending, setDoctorEmailSending] = useState(false)
  const [intakeUrl, setIntakeUrl]           = useState(null)
  const [intakeSending, setIntakeSending]   = useState(false)
  const [scanUploading, setScanUploading]   = useState(false)
  const [scanFilename, setScanFilename]     = useState(patient.intake_scan_filename || null)
  const [scanUploadedAt, setScanUploadedAt] = useState(patient.intake_scan_uploaded_at || null)
  const scanInputRef = useRef(null)
  const [patientData, setPatientData]       = useState(patient)

  // Documents
  const [documents, setDocuments]     = useState([])
  const [docUploading, setDocUploading] = useState(false)
  const [docCategory, setDocCategory] = useState('Other')
  const [docDragOver, setDocDragOver] = useState(false)
  const fileInputRef                  = useRef(null)

  // Voice dictation
  const [voiceField, setVoiceField]     = useState(null)
  const [voiceSupported]                = useState(() => !!(window.SpeechRecognition || window.webkitSpeechRecognition))
  const recognitionRef                  = useRef(null)
  const interimRef                      = useRef('')

  const stopVoice = useCallback(() => {
    recognitionRef.current?.stop()
    recognitionRef.current = null
    interimRef.current = ''
    setVoiceField(null)
  }, [])

  const startVoice = useCallback((fieldName) => {
    if (!voiceSupported) return
    if (voiceField === fieldName) { stopVoice(); return }
    stopVoice()

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    const rec = new SR()
    rec.lang = 'en-US'
    rec.continuous = true
    rec.interimResults = true
    interimRef.current = ''

    rec.onresult = (e) => {
      let final = ''; let interim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript
        if (e.results[i].isFinal) final += t
        else interim += t
      }
      interimRef.current = interim
      if (final) {
        setSessionForm(prev => ({
          ...prev,
          [fieldName]: (prev[fieldName] ? prev[fieldName] + ' ' : '') + final.trim(),
        }))
      }
    }

    rec.onerror = () => stopVoice()
    rec.onend   = () => { if (recognitionRef.current === rec) stopVoice() }

    recognitionRef.current = rec
    setVoiceField(fieldName)
    rec.start()
  }, [voiceField, sessionForm, stopVoice, voiceSupported])

  useEffect(() => () => recognitionRef.current?.stop(), [])

  const p = patientData

  useEffect(() => { fetchAll() }, [])
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(API + '/patients/' + patient.id + '/messages/unread')
        .then(res => setUnreadCount(res.data.unread)).catch(() => {})
    }, 10000)
    return () => clearInterval(interval)
  }, [])

  const fetchAll = () => {
    axios.get(API + '/patients/' + patient.id + '/progress').then(res => setProgress(res.data)).catch(() => {})
    axios.get(API + '/patients/' + patient.id + '/goals').then(res => setGoals(res.data)).catch(() => {})
    axios.get(API + '/patients/' + patient.id + '/exercises').then(res => setExercises(res.data)).catch(() => {})
    axios.get(API + '/patients/' + patient.id + '/exercise-logs').then(res => setExerciseLogs(res.data)).catch(() => {})
    axios.get(API + '/patients/' + patient.id + '/exercise-compliance').then(res => setCompliance(res.data)).catch(() => {})
    axios.get(API + '/patients/' + patient.id + '/messages/unread').then(res => setUnreadCount(res.data.unread)).catch(() => {})
    axios.get(API + '/patients/' + patient.id).then(res => setPatientData(res.data)).catch(() => {})
  }

  const markMessagesRead = () => {
    axios.get(API + '/patients/' + patient.id + '/messages').then(res => {
      res.data.filter(m => !m.is_read && m.sender_role === 'patient').forEach(m => {
        axios.put(API + '/messages/' + m.id + '/read').catch(() => {})
      })
    })
  }

  const handleLogSession = () => {
    const sessionNum = parseInt(sessionForm.session_number)
    const painScore  = parseFloat(sessionForm.pain_score)
    const rom        = sessionForm.rom !== '' ? parseFloat(sessionForm.rom) : null
    // exercises_completed: derive from checklist if exercises exist, else use manual field
    let exComp = null
    if (exercises.length > 0) {
      const checked = exercises.filter(ex => sessionChecklist[ex.id]).length
      exComp = Math.round((checked / exercises.length) * 100)
    } else if (sessionForm.exercises_completed !== '') {
      exComp = parseFloat(sessionForm.exercises_completed)
    }
    if (!sessionForm.session_number || isNaN(sessionNum)) { alert('Please enter a session number.'); return }
    if (!sessionForm.date)                                { alert('Please select a session date.'); return }
    if (sessionForm.pain_score === '' || isNaN(painScore)){ alert('Please select a pain score.'); return }
    axios.post(API + '/sessions', {
      patient_id: patient.id,
      session_number: sessionNum,
      date: sessionForm.date,
      pain_score: painScore,
      rom: rom !== null && !isNaN(rom) ? rom : null,
      exercises_completed: exComp,
      notes: sessionForm.notes,
      soap_subjective: sessionForm.soap_subjective,
      soap_objective: sessionForm.soap_objective,
      soap_assessment: sessionForm.soap_assessment,
      soap_plan: sessionForm.soap_plan,
    }).then(() => {
      setSessionForm({
        session_number: '', date: '', pain_score: '', rom: '',
        exercises_completed: '', notes: '', soap_subjective: '',
        soap_objective: '', soap_assessment: '', soap_plan: '',
      })
      setSessionChecklist({})
      fetchAll()
    }).catch(err => {
      console.error(err)
      alert(err.response?.data?.detail || 'Failed to log session — please try again.')
    })
  }

  const handleSoapDraft = async () => {
    const quickNote = sessionForm.notes || sessionForm.soap_subjective
    if (!quickNote && !sessionForm.pain_score) {
      alert('Add a quick session note or pain score first — Caleb needs something to work with.')
      return
    }
    setSoapDrafting(true)
    setNotesMode('soap')
    try {
      const res = await axios.post(API + '/ai/soap-draft', {
        patient_id: patient.id,
        quick_note: quickNote || `Pain score: ${sessionForm.pain_score}/10. ROM: ${sessionForm.rom || 'not recorded'}.`,
        pain_score: sessionForm.pain_score ? parseFloat(sessionForm.pain_score) : null,
        rom: sessionForm.rom ? parseFloat(sessionForm.rom) : null,
        session_number: sessionForm.session_number ? parseInt(sessionForm.session_number) : null,
      })
      const d = res.data
      setSessionForm(prev => ({
        ...prev,
        soap_subjective: d.subjective || prev.soap_subjective,
        soap_objective:  d.objective  || prev.soap_objective,
        soap_assessment: d.assessment || prev.soap_assessment,
        soap_plan:       d.plan       || prev.soap_plan,
      }))
    } catch (err) {
      alert(err.response?.data?.detail || 'SOAP draft failed — try again.')
    } finally {
      setSoapDrafting(false)
    }
  }

  const handlePlateauInterventions = async () => {
    setPlateauLoading(true)
    setPlateauError(false)
    try {
      const res = await axios.post(API + '/ai/plateau-interventions', { patient_id: patient.id })
      const items = res.data.interventions || []
      if (items.length === 0) { setPlateauError(true); return }
      setPlateauInterventions(items)
    } catch {
      setPlateauError(true)
    } finally {
      setPlateauLoading(false)
    }
  }

  const handleEmailDoctor = async () => {
    if (!window.confirm(`Send a progress report to Dr. ${p.referring_doctor || 'the referring physician'}?`)) return
    setDoctorEmailSending(true)
    try {
      await axios.post(API + '/patients/' + patient.id + '/email-doctor')
      onToast && onToast('Progress report sent to referring doctor.')
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send report — check that the doctor email is on file.')
    } finally {
      setDoctorEmailSending(false)
    }
  }

  const handleSaveReferralDoctor = async () => {
    setReferralSaving(true)
    try {
      await axios.patch(API + '/patients/' + patient.id + '/referring-doctor', referralForm)
      onToast && onToast('Referring doctor info saved.')
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save — please try again.')
    } finally {
      setReferralSaving(false)
    }
  }

  const handleThankYouLetter = async () => {
    if (!window.confirm(`Send a discharge thank-you letter to Dr. ${referralForm.referring_doctor || 'the referring physician'}?`)) return
    setThankYouSending(true)
    try {
      await axios.post(API + '/patients/' + patient.id + '/email-doctor')
      onToast && onToast('Discharge thank-you letter sent to referring doctor.', 'success')
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send — check that the doctor email is on file.')
    } finally {
      setThankYouSending(false)
    }
  }

  const handleAddGoal = () => {
    if (!goalForm.goal_text) { alert('Please enter a goal.'); return }
    axios.post(API + '/goals', { ...goalForm, patient_id: patient.id })
      .then(() => { setGoalForm({ goal_text: '', target_value: '', set_by: 'Therapist' }); fetchAll() })
      .catch(err => {
        console.error(err)
        alert(err.response?.data?.detail || 'Failed to add goal — please try again.')
      })
  }

  const handleCompleteGoal = (id) => axios.put(API + '/goals/' + id + '/complete').then(() => fetchAll())
  const handleDeleteGoal   = (id) => { if (!window.confirm('Remove this goal?')) return; axios.delete(API + '/goals/' + id).then(() => fetchAll()) }

  const handleAddExercise = () => {
    const finalName = exerciseForm.exercise_name === 'Other' ? exerciseForm.exercise_name_custom : exerciseForm.exercise_name
    if (!finalName) { alert('Please select an exercise.'); return }
    const week = parseInt(exerciseForm.week_number)
    const sets = parseInt(exerciseForm.sets)
    const reps = parseInt(exerciseForm.reps)
    if (!exerciseForm.week_number || isNaN(week)) { alert('Please enter a week number.'); return }
    if (!exerciseForm.sets || isNaN(sets))         { alert('Please enter the number of sets.'); return }
    if (!exerciseForm.reps || isNaN(reps))         { alert('Please enter the number of reps.'); return }
    axios.post(API + '/exercises', {
      ...exerciseForm, exercise_name: finalName, patient_id: patient.id,
      week_number: week, sets, reps,
    }).then(() => {
      setExerciseForm({ week_number: '', exercise_name: '', exercise_name_custom: '', sets: '', reps: '', notes: '', video_url: '' })
      fetchAll()
    }).catch(err => {
      console.error(err)
      alert(err.response?.data?.detail || 'Failed to add exercise — please try again.')
    })
  }

  const handleDeleteExercise = (id) => {
    if (!window.confirm('Remove this exercise?')) return
    axios.delete(API + '/exercises/' + id).then(() => fetchAll())
  }

  //  Discharge handlers 
  const handleDischarge = ({ reason, notes }) => {
    axios.post(API + '/patients/' + patient.id + '/discharge', { reason, notes })
      .then(() => {
        setShowDischargeModal(false)
        fetchAll()
        if (onToast) onToast(`${patient.name} has been discharged`, 'success')
      })
      .catch(() => { if (onToast) onToast('Failed to discharge patient', 'error') })
  }

  const handleUndischarge = () => {
    if (!window.confirm(`Reinstate ${patient.name} as an active patient?`)) return
    axios.post(API + '/patients/' + patient.id + '/undischarge')
      .then(() => { fetchAll(); if (onToast) onToast(`${patient.name} reinstated`, 'success') })
      .catch(() => { if (onToast) onToast('Failed to reinstate patient', 'error') })
  }

  //  Documents 
  const fetchDocuments = useCallback(() => {
    axios.get(API + '/patients/' + patient.id + '/documents')
      .then(res => setDocuments(res.data || [])).catch(() => {})
  }, [patient.id])

  useEffect(() => { fetchDocuments() }, [fetchDocuments])

  const handleUploadFile = async (file) => {
    if (!file) return
    setDocUploading(true)
    const form = new FormData()
    form.append('file', file)
    form.append('category', docCategory)
    form.append('clinic_id', patient.clinic_id || '')
    form.append('uploaded_by', 'Staff')
    try {
      await axios.post(API + '/patients/' + patient.id + '/documents', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      fetchDocuments()
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed')
    } finally {
      setDocUploading(false)
    }
  }

  const handleDeleteDoc = async (docId, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return
    await axios.delete(API + '/documents/' + docId).catch(() => {})
    fetchDocuments()
  }

  const handleSendIntake = () => {
    setIntakeSending(true)
    axios.post(API + '/patients/' + patient.id + '/intake-link')
      .then(res => {
        setIntakeUrl(res.data.intake_url)
        if (res.data.email_sent)          { if (onToast) onToast('Intake form emailed to ' + res.data.patient_email, 'success') }
        else if (!res.data.patient_email) { if (onToast) onToast('Link generated — no email on file, copy below', 'info') }
        else                              { if (onToast) onToast('Link generated — email could not be sent, copy below', 'warning') }
      })
      .catch(() => { if (onToast) onToast('Failed to generate intake link', 'error') })
      .finally(() => setIntakeSending(false))
  }

  const handlePrintIntake = () => {
    const win = window.open('', '_blank', 'width=900,height=1100')
    win.document.write(buildPrintableIntakeHTML(patient.name, patient.injury_type))
    win.document.close()
    win.focus()
    setTimeout(() => win.print(), 400)
  }

  const handleScanUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setScanUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await axios.post(API + '/patients/' + patient.id + '/intake-scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setScanFilename(res.data.filename)
      setScanUploadedAt(new Date().toISOString())
      if (onToast) onToast('Intake scan uploaded successfully', 'success')
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed'
      if (onToast) onToast(msg, 'error')
    } finally {
      setScanUploading(false)
      if (scanInputRef.current) scanInputRef.current.value = ''
    }
  }

  const today      = new Date().toISOString().split('T')[0]
  const sessions   = progress?.sessions || []
  const prediction = progress?.prediction
  const totalSessions = prediction ? prediction.predicted_sessions_remaining + sessions.length : 20
  const recoveryPct   = sessions.length > 0 ? Math.min(Math.round((sessions.length / totalSessions) * 100), 99) : 0
  const latest        = sessions[sessions.length - 1]
  const first         = sessions[0]
  const painReduced   = first && latest ? Math.round(((first.pain_score - latest.pain_score) / first.pain_score) * 100) : 0
  const isProgressing = prediction && !prediction.plateau_flag

  const chartData  = sessions.map(s => ({ session: 'S' + s.session_number, 'Pain Score': s.pain_score, 'ROM': s.rom, 'Exercises %': s.exercises_completed }))
  const weekGroups = exercises.reduce((acc, ex) => { const w = 'Week ' + ex.week_number; if (!acc[w]) acc[w] = []; acc[w].push(ex); return acc }, {})

  const isFrontDesk = userRole === 'front_desk'
  const tabs = isFrontDesk
    ? ['Overview', 'Documents', 'Messages', 'Intake']
    : ['Overview', 'Goals', 'Treatment Plan', 'Log Session', 'Referral', 'Documents', 'Messages', 'Intake', 'Billing']

  const patientGroup   = INJURY_TO_EXERCISE_GROUP[patient.injury_type]
  const libraryEntries = Object.entries(EXERCISE_LIBRARY)
  const sortedLibrary  = patientGroup
    ? [...libraryEntries.filter(([g]) => g === patientGroup), ...libraryEntries.filter(([g]) => g !== patientGroup)]
    : libraryEntries

  const recovColor = recoveryPct < 33 ? '#f87171' : recoveryPct < 66 ? '#fbbf24' : '#34d399'

  return (
    <div style={{ fontFamily: FONT }}>
      <style>{CSS}</style>

      {/*  Discharge modal  */}
      {showDischargeModal && (
        <DischargeModal
          patient={p}
          onConfirm={handleDischarge}
          onCancel={() => setShowDischargeModal(false)}
        />
      )}

      {/*  Patient header  */}
      <div className="pd-header" style={{
        background: `linear-gradient(152deg, ${TEAL_DARK} 0%, #065f46 40%, ${PURPLE_DARK} 100%)`,
        borderRadius: '20px',
        padding: '28px 32px',
        marginBottom: '20px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Blobs */}
        <div style={{ position: 'absolute', top: '-40%', right: '-6%', width: '300px', height: '300px', background: 'radial-gradient(circle at 40% 40%, rgba(110,231,183,0.18) 0%, transparent 70%)', borderRadius: '999px', animation: 'blob1 15s ease-in-out infinite', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-50%', left: '-4%', width: '280px', height: '280px', background: 'radial-gradient(circle at 60% 60%, rgba(52,211,153,0.13) 0%, transparent 70%)', borderRadius: '999px', animation: 'blob2 20s ease-in-out infinite', pointerEvents: 'none' }} />
        {/* Grid */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none', borderRadius: '20px' }} />

        <div style={{ position: 'relative', zIndex: 2 }}>

          {/* Discharged banner */}
          {p.is_discharged && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.3)',
              borderRadius: '8px', padding: '7px 14px',
              fontSize: '13px', color: '#4ade80', fontWeight: '600',
              marginBottom: '16px',
            }}>
              <span> Discharged</span>
              {p.discharged_at && (
                <span style={{ fontWeight: '400', color: 'rgba(74,222,128,0.75)' }}>
                  {new Date(p.discharged_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
              {p.discharge_notes && (
                <span style={{ fontWeight: '400', color: 'rgba(74,222,128,0.65)', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  — {p.discharge_notes}
                </span>
              )}
            </div>
          )}

          {/* Top row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              {/* Avatar */}
              <div style={{
                width: '54px', height: '54px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.18)',
                border: '2px solid rgba(255,255,255,0.28)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: DISPLAY, fontStyle: 'italic',
                fontSize: '18px', fontWeight: '400', color: 'white', flexShrink: 0,
              }}>
                {patient.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <div style={{ fontFamily: DISPLAY, fontSize: '24px', fontWeight: '400', color: 'white', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                  {patient.name}
                </div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(110,231,183,0.15)', color: '#6ee7b7', fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '100px', border: '1px solid rgba(110,231,183,0.25)' }}>
                    {patient.injury_type}
                  </span>
                  {calcAge((patientData || patient).dob, (patientData || patient).age) != null && <span>Age {calcAge((patientData || patient).dob, (patientData || patient).age)}</span>}
                  <span>·</span>
                  <span>Starting pain: {(patientData || patient).intake_pain_score}/10</span>
                  {(patientData || patient).reason_for_injury && <><span>·</span><span>{(patientData || patient).reason_for_injury}</span></>}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {ff.pdf ? (
                <button
                  onClick={() => window.open(API + '/patients/' + patient.id + '/report')}
                  className="pd-action-btn"
                >
                  Download Report
                </button>
              ) : (
                <button title="Upgrade to unlock PDF reports" className="pd-action-btn"
                  style={{ opacity: 0.45, cursor: 'default' }}>
                   Download Report
                </button>
              )}
              <button
                onClick={handleEmailDoctor}
                disabled={doctorEmailSending}
                title={p.referring_doctor_email ? `Send progress report to ${p.referring_doctor_email}` : 'Add referring doctor email in patient info first'}
                className="pd-action-btn"
                style={{ opacity: doctorEmailSending ? 0.6 : 1 }}
              >
                {doctorEmailSending ? 'Sending…' : 'Email Doctor'}
              </button>
              <button
                onClick={() => {
                  axios.post(API + '/patients/' + patient.id + '/send-reminder')
                    .then(res => {
                      if (onToast) onToast(res.data.message || 'Reminder sent!', res.data.success ? 'success' : 'error')
                      else alert(res.data.message)
                    })
                    .catch(() => { if (onToast) onToast('Failed to send reminder', 'error') })
                }}
                className="pd-action-btn"
              >
                Send Reminder
              </button>

              {/* Discharge / Reinstate — therapists + admins only */}
              {!isFrontDesk && (
                p.is_discharged ? (
                  <button onClick={handleUndischarge} className="pd-action-btn discharged-btn">
                    ↩ Reinstate
                  </button>
                ) : (
                  <button onClick={() => setShowDischargeModal(true)} className="pd-action-btn">
                    Discharge
                  </button>
                )
              )}

              {/* Caleb AI */}
              {ff.caleb ? (
                <button onClick={() => openCaleb({
                  type: 'patient',
                  patientId: patient.id,
                  patientName: patient.name,
                  extra: { injuryType: patient.injury_type, diagnosis: patient.diagnosis },
                })} style={{
                  padding: '7px 16px', borderRadius: '8px', border: '1.5px solid rgba(110,231,183,0.45)',
                  background: calebPanelOpen ? 'rgba(110,231,183,0.18)' : 'rgba(255,255,255,0.1)',
                  color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '700',
                  fontFamily: FONT, display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s',
                }}>
                  <span style={{ fontSize: '16px' }}></span> Ask Caleb AI
                </button>
              ) : (
                <button title="Upgrade to unlock Caleb AI" style={{
                  padding: '7px 16px', borderRadius: '8px', border: '1.5px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)', cursor: 'default',
                  fontSize: '13px', fontWeight: '700', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <span style={{ fontSize: '14px' }}></span> Ask Caleb AI
                </button>
              )}
            </div>
          </div>

          {/* Stats strip */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gap: '1px',
            background: 'rgba(255,255,255,0.12)',
            borderRadius: '14px',
            overflow: 'hidden',
          }}>
            {[
              { label: 'Recovery',    value: recoveryPct + '%',                                            color: recovColor },
              { label: 'Sessions',    value: sessions.length + ' / ' + totalSessions,                     color: 'white' },
              { label: 'Pain reduced',value: painReduced > 0 ? '-' + painReduced + '%' : '—',             color: painReduced > 0 ? '#34d399' : 'rgba(255,255,255,0.35)' },
              ff.discharge ? { label: 'Remaining',   value: prediction ? prediction.predicted_sessions_remaining : '—',   color: 'white' } : null,
              ff.plateau   ? { label: 'Status',      value: sessions.length === 0 ? 'No data' : isProgressing ? 'Progressing' : 'Plateau', color: sessions.length === 0 ? 'rgba(255,255,255,0.35)' : isProgressing ? '#34d399' : '#f87171' } : null,
              ff.compliance ? { label: 'Compliance',  value: compliance ? compliance.compliance_pct + '%' : '—',           color: compliance && compliance.compliance_pct >= 70 ? '#34d399' : compliance && compliance.compliance_pct >= 40 ? '#fbbf24' : '#f87171' } : null,
            ].filter(Boolean).map(s => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.07)', padding: '16px 18px' }}>
                <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: '24px', color: s.color, lineHeight: 1, letterSpacing: '-0.01em' }}>{s.value}</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.38)', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: '700' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Plateau intervention card */}
      {ff.plateau && prediction && prediction.plateau_flag && sessions.length > 0 && (
        <div style={{
          background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '12px',
          padding: '16px 20px', marginBottom: '12px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
            <div>
              <div style={{ fontWeight: '700', color: '#9a3412', fontSize: '14px', marginBottom: '4px' }}>
                Plateau detected
              </div>
              <div style={{ fontSize: '13px', color: '#7c2d12', lineHeight: 1.5 }}>
                Pain scores haven't changed significantly over recent sessions.
              </div>
            </div>
            {!plateauInterventions && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <button
                  onClick={handlePlateauInterventions}
                  disabled={plateauLoading}
                  style={{
                    flexShrink: 0, padding: '7px 14px', borderRadius: '8px', border: 'none',
                    background: plateauLoading ? '#e2e8f0' : '#9a3412',
                    color: plateauLoading ? '#94a3b8' : 'white',
                    cursor: plateauLoading ? 'not-allowed' : 'pointer',
                    fontSize: '12px', fontWeight: '700', fontFamily: FONT, whiteSpace: 'nowrap',
                  }}
                >
                  {plateauLoading ? 'Loading…' : plateauError ? 'Retry' : 'Get Caleb suggestions'}
                </button>
                {plateauError && (
                  <span style={{ fontSize: '11px', color: '#dc2626' }}>Failed — check API connection and try again</span>
                )}
              </div>
            )}
          </div>
          {plateauInterventions && plateauInterventions.length > 0 && (
            <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {plateauInterventions.map((iv, i) => {
                const catColors = {
                  exercise_progression: { bg: '#dbeafe', color: '#1e40af' },
                  manual_therapy:       { bg: '#f3e8ff', color: '#6b21a8' },
                  reassessment:         { bg: '#fef9c3', color: '#854d0e' },
                  patient_education:    { bg: '#dcfce7', color: '#14532d' },
                  referral:             { bg: '#ffe4e6', color: '#9f1239' },
                }
                const cc = catColors[iv.category] || { bg: '#f1f5f9', color: '#475569' }
                const catLabel = (iv.category || '').replace(/_/g, ' ')
                return (
                  <div key={i} style={{
                    background: 'white', borderRadius: '8px', padding: '12px 14px',
                    border: '1px solid #fed7aa',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>{iv.title}</span>
                      <span style={{
                        fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '99px',
                        background: cc.bg, color: cc.color, textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>{catLabel}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>{iv.rationale}</div>
                  </div>
                )
              })}
              <button
                onClick={() => setPlateauInterventions(null)}
                style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: '#94a3b8', fontSize: '12px', cursor: 'pointer', fontFamily: FONT }}
              >Dismiss</button>
            </div>
          )}
        </div>
      )}

      {/*  Tabs  */}
      <div style={{ background: 'rgba(255,255,255,0.92)', borderRadius: '14px 14px 0 0', border: 'none', borderBottom: '1px solid rgba(124,58,237,0.18)' }}>
        <div className="tab-scroll" style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {tabs.map(tab => (
            <button key={tab} className="pd-tab" onClick={() => { setActiveTab(tab); if (tab === 'Messages') { setUnreadCount(0); markMessagesRead() } }} style={{
              padding: '15px 22px', fontSize: '14px', fontWeight: activeTab === tab ? '700' : '500',
              border: 'none', borderBottom: activeTab === tab ? `2px solid ${TEAL}` : '2px solid transparent',
              background: 'none', cursor: 'pointer',
              color: activeTab === tab ? TEAL : '#94a3b8',
              whiteSpace: 'nowrap', flexShrink: 0, fontFamily: FONT,
              display: 'flex', alignItems: 'center', gap: '6px',
              letterSpacing: activeTab === tab ? '-0.01em' : '0',
            }}>
              {tab}
              {tab === 'Messages' && unreadCount > 0 && (
                <span style={{ background: '#ef4444', color: 'white', borderRadius: '99px', fontSize: '10px', fontWeight: '700', padding: '1px 6px', lineHeight: '1.6' }}>{unreadCount}</span>
              )}
              {tab === 'Intake' && p.intake_completed && (
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/*  Tab content  */}
      <div className="pd-tab-content" style={{ borderTop: 'none', borderRadius: '0 0 14px 14px', padding: '28px', marginBottom: '24px' }}>

        {activeTab === 'Overview' && (
          <div className="pd-overview-grid pd-wide-2col" style={{ display: 'grid', gridTemplateColumns: p.pain_body_regions ? '1fr 200px' : '1fr', gap: '32px', alignItems: 'start' }}>
            <div>
              {chartData.length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                  {sectionLabel('Progress over time')}
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0fdfa" />
                      <XAxis dataKey="session" tick={{ fontSize: 12, fill: '#94a3b8', fontFamily: FONT }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: '#94a3b8', fontFamily: FONT }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ borderRadius: '10px', border: '1.5px solid rgba(124,58,237,0.25)', fontSize: '13px', fontFamily: FONT }} />
                      <Legend wrapperStyle={{ fontSize: '13px', fontFamily: FONT }} />
                      <Line type="monotone" dataKey="Pain Score" stroke={PURPLE} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="ROM" stroke={TEAL} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Exercises %" stroke="#818cf8" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              {sessions.length > 0 && sessions.some(s => s.notes || s.soap_subjective) && (
                <div>
                  {sectionLabel('Session notes')}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                    {sessions.slice().reverse().map(s => (s.notes || s.soap_subjective) && (
                      <div key={s.id} className="pd-card" style={{ border: '1.5px solid rgba(124,58,237,0.25)', borderRadius: '12px', padding: '16px 18px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: TEAL_DARK, background: TEAL_LIGHT, padding: '3px 10px', borderRadius: '99px', border: `1px solid ${TEAL_MID}` }}>Session {s.session_number}</span>
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>{s.date}</span>
                        </div>
                        {s.notes && <p style={{ fontSize: '14px', color: '#475569', margin: 0, lineHeight: '1.65' }}>{s.notes}</p>}
                        {s.soap_subjective && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {[['S', s.soap_subjective], ['O', s.soap_objective], ['A', s.soap_assessment], ['P', s.soap_plan]].map(([label, val]) => val && (
                              <div key={label}>
                                <span style={{ fontSize: '11px', fontWeight: '700', color: TEAL, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                                <p style={{ fontSize: '14px', color: '#475569', margin: '2px 0 0', lineHeight: '1.65' }}>{val}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {p.pain_body_regions && (() => {
              let regions = []
              try { regions = JSON.parse(p.pain_body_regions) } catch {}
              return regions.length > 0 ? (
                <div style={{ background: 'rgba(248,250,252,0.78)', borderRadius: '14px', padding: '16px', border: '1.5px solid rgba(124,58,237,0.25)' }}>
                  {sectionLabel('Pain map')}
                  <BodyMap highlightedRegions={regions} readOnly />
                </div>
              ) : null
            })()}
          </div>
        )}

        {activeTab === 'Goals' && (
          <div>
            <div style={{ background: TEAL_LIGHT, borderRadius: '12px', padding: '18px', marginBottom: '22px', border: `1.5px solid ${TEAL_MID}` }}>
              {sectionLabel('Add goal')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                <input placeholder="e.g. Walk up stairs without pain" value={goalForm.goal_text} onChange={e => setGoalForm({ ...goalForm, goal_text: e.target.value })} style={inputStyle} />
                <input placeholder="Target value (optional)" value={goalForm.target_value} onChange={e => setGoalForm({ ...goalForm, target_value: e.target.value })} style={inputStyle} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select value={goalForm.set_by} onChange={e => setGoalForm({ ...goalForm, set_by: e.target.value })} style={{ ...inputStyle, width: 'auto' }}>
                    <option value="Therapist">Set by Therapist</option>
                    <option value="Patient">Set by Patient</option>
                  </select>
                  <button onClick={handleAddGoal} className="pd-btn" style={{ padding: '10px 22px', background: TEAL, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: FONT, transition: 'all .15s' }}>Add</button>
                </div>
              </div>
            </div>
            {goals.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px', fontSize: '15px' }}>No goals yet.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {goals.map(g => (
                <div key={g.id} className="pd-card" style={{ display: 'flex', alignItems: 'center', gap: '12px', border: `1.5px solid ${g.is_complete ? TEAL_MID : '#e2e8f0'}`, borderRadius: '12px', padding: '14px 18px', background: g.is_complete ? TEAL_LIGHT : 'white' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: `2px solid ${g.is_complete ? TEAL : '#cbd5e0'}`, background: g.is_complete ? TEAL : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {g.is_complete && <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><polyline points="2,7 6,11 12,3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: g.is_complete ? '#64748b' : '#0f172a', textDecoration: g.is_complete ? 'line-through' : 'none' }}>{g.goal_text}</div>
                    {g.target_value && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '1px' }}>Target: {g.target_value}</div>}
                  </div>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Set by {g.set_by}</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {!g.is_complete && <button onClick={() => handleCompleteGoal(g.id)} style={{ padding: '5px 12px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '99px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', fontFamily: FONT }}>Done</button>}
                    <button onClick={() => handleDeleteGoal(g.id)} style={{ padding: '5px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '99px', cursor: 'pointer', fontSize: '12px', fontFamily: FONT }}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'Treatment Plan' && (
          <div>
            {ff.hep ? (
            <div style={{ background: TEAL_LIGHT, borderRadius: '12px', padding: '18px', marginBottom: '22px', border: `1.5px solid ${TEAL_MID}` }}>
              {sectionLabel('Add exercise')}
              <div className="pd-form-grid" style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <input placeholder="Week" type="number" value={exerciseForm.week_number} onChange={e => setExerciseForm({ ...exerciseForm, week_number: e.target.value })} style={inputStyle} />
                <input placeholder="Sets" type="number" value={exerciseForm.sets} onChange={e => setExerciseForm({ ...exerciseForm, sets: e.target.value })} style={inputStyle} />
                <input placeholder="Reps" type="number" value={exerciseForm.reps} onChange={e => setExerciseForm({ ...exerciseForm, reps: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ marginBottom: '8px' }}>
                <select value={exerciseForm.exercise_name} onChange={e => setExerciseForm({ ...exerciseForm, exercise_name: e.target.value, exercise_name_custom: '' })} style={{ ...inputStyle, color: exerciseForm.exercise_name ? '#1e293b' : '#94a3b8', width: '100%' }}>
                  <option value="" disabled>Select exercise{patientGroup ? ` — ${patientGroup} shown first` : ''}...</option>
                  {sortedLibrary.map(([group, exList]) => (
                    <optgroup key={group} label={group === patientGroup ? ` ${group}` : group}>
                      {exList.map(ex => <option key={ex} value={ex}>{ex}</option>)}
                    </optgroup>
                  ))}
                  <optgroup label=""><option value="Other">Other (type below)</option></optgroup>
                </select>
                {exerciseForm.exercise_name === 'Other' && (
                  <input placeholder="Enter exercise name..." value={exerciseForm.exercise_name_custom} onChange={e => setExerciseForm({ ...exerciseForm, exercise_name_custom: e.target.value })} style={{ ...inputStyle, marginTop: '6px' }} />
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input placeholder="Notes (optional)" value={exerciseForm.notes} onChange={e => setExerciseForm({ ...exerciseForm, notes: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="Video URL (optional)" value={exerciseForm.video_url} onChange={e => setExerciseForm({ ...exerciseForm, video_url: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
              </div>
              <button onClick={handleAddExercise} className="pd-btn" style={{ padding: '10px 22px', background: TEAL, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: FONT, transition: 'all .15s' }}>Add Exercise</button>
            </div>
            ) : (
            <div style={{ background: 'rgba(248,250,252,0.78)', borderRadius: '12px', padding: '24px', marginBottom: '22px', border: '1.5px dashed #cbd5e1', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', marginBottom: '8px' }}></div>
              <div style={{ fontWeight: '700', color: '#475569', marginBottom: '4px', fontFamily: FONT }}>Home Exercise Program Builder</div>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>Upgrade your plan to add and manage patient exercises.</div>
            </div>
            )}

            {ff.compliance && compliance && compliance.total_exercises > 0 && (
              <div style={{
                background: `linear-gradient(152deg, ${PURPLE_DARK} 0%, ${TEAL_DARK} 100%)`,
                borderRadius: '14px', padding: '20px 24px', marginBottom: '20px',
                display: 'flex', gap: '32px', alignItems: 'center',
                position: 'relative', overflow: 'hidden',
              }} className="pd-compliance-strip">
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none' }} />
                {[
                  { val: compliance.compliance_pct + '%', label: 'This week', color: compliance.compliance_pct >= 70 ? '#34d399' : compliance.compliance_pct >= 40 ? '#fbbf24' : '#f87171' },
                  { val: compliance.logged_this_week + ' / ' + compliance.total_exercises, label: 'Logged this week', color: 'white' },
                  { val: compliance.total_logs, label: 'Total logs', color: TEAL_MID },
                ].map(s => (
                  <div key={s.label} style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: '28px', color: s.color, lineHeight: 1 }}>{s.val}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.09em', marginTop: '6px', fontWeight: '700' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {Object.keys(weekGroups).length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px', fontSize: '15px' }}>No exercises yet.</div>}
            {Object.keys(weekGroups).sort().map(week => (
              <div key={week} style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ height: '1px', flex: 1, background: '#e2e8f0' }} />
                  <span style={{ fontSize: '11px', fontWeight: '700', color: TEAL, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{week}</span>
                  <div style={{ height: '1px', flex: 1, background: '#e2e8f0' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {weekGroups[week].map(ex => {
                    const exLogs    = exerciseLogs.filter(l => l.exercise_id === ex.id).slice(0, 3)
                    const loggedToday = exerciseLogs.some(l => l.exercise_id === ex.id && l.log_date === today)
                    return (
                      <div key={ex.id} className="pd-card" style={{ border: `1.5px solid ${loggedToday ? TEAL_MID : '#e2e8f0'}`, borderRadius: '12px', padding: '14px 18px', background: loggedToday ? TEAL_LIGHT : 'white' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: ex.notes || ex.video_url ? '4px' : 0 }}>
                              <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{ex.exercise_name}</span>
                              <span style={{ fontSize: '12px', fontWeight: '700', color: TEAL_DARK, background: TEAL_LIGHT, padding: '2px 8px', borderRadius: '99px', border: `1px solid ${TEAL_MID}` }}>{ex.sets} × {ex.reps}</span>
                              {loggedToday && (
                                <span style={{ fontSize: '11px', fontWeight: '700', color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: '99px', border: '1px solid #bbf7d0' }}> Logged today</span>
                              )}
                            </div>
                            {ex.notes && <div style={{ fontSize: '13px', color: '#64748b' }}>{ex.notes}</div>}
                            {ex.video_url && <a href={ex.video_url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>Watch video</a>}
                            {exLogs.length > 0 && (
                              <div style={{ marginTop: '8px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                {exLogs.map(log => (
                                  <span key={log.id} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: '#f1f5f9', color: '#64748b' }}>
                                    {log.log_date} · {log.difficulty}/5
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button onClick={() => handleDeleteExercise(ex.id)} style={{ padding: '5px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '99px', cursor: 'pointer', fontSize: '12px', fontFamily: FONT, marginLeft: '12px', flexShrink: 0 }}>Remove</button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'Log Session' && (
          <div className="pd-session-form" style={{ maxWidth: '540px' }}>
            {sectionLabel('Log new session')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '16px' }}>
              <div className="pd-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Session number</label>
                  <input placeholder="e.g. 4" type="number" value={sessionForm.session_number} onChange={e => setSessionForm({ ...sessionForm, session_number: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input type="date" value={sessionForm.date} onChange={e => setSessionForm({ ...sessionForm, date: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Pain score (0–10) <span style={{ color: '#ef4444' }}>*</span></label>
                  <select value={sessionForm.pain_score} onChange={e => setSessionForm({ ...sessionForm, pain_score: e.target.value })} style={{ ...inputStyle, color: sessionForm.pain_score !== '' ? '#1e293b' : '#94a3b8' }}>
                    <option value="" disabled>Select pain level...</option>
                    {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                      <option key={n} value={n}>{n} — {['No pain','Minimal','Mild','Uncomfortable','Moderate','Noticeable','Moderate-severe','Severe','Very severe','Intense','Worst possible'][n]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>ROM / degrees <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                  <input placeholder="e.g. 75" type="number" value={sessionForm.rom} onChange={e => setSessionForm({ ...sessionForm, rom: e.target.value })} style={inputStyle} />
                </div>
              </div>

              {/* Exercise checklist */}
              <div style={{ borderTop: '1.5px solid rgba(13,148,136,0.2)', paddingTop: '14px' }}>
                <label style={{ ...labelStyle, display: 'block', marginBottom: '10px' }}>
                  Exercises completed this session
                  {exercises.length > 0 && (
                    <span style={{ marginLeft: 8, fontWeight: 400, color: '#94a3b8', fontSize: '11px' }}>
                      {exercises.filter(ex => sessionChecklist[ex.id]).length} / {exercises.length} checked
                    </span>
                  )}
                </label>
                {exercises.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {/* Quick-select row */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                      <button type="button" onClick={() => { const all = {}; exercises.forEach(ex => { all[ex.id] = true }); setSessionChecklist(all) }}
                        style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '1.5px solid rgba(13,148,136,0.2)', background: '#f8fafc', color: '#475569', cursor: 'pointer', fontFamily: FONT, fontWeight: 600 }}>
                        All done
                      </button>
                      <button type="button" onClick={() => setSessionChecklist({})}
                        style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '1.5px solid rgba(13,148,136,0.2)', background: '#f8fafc', color: '#475569', cursor: 'pointer', fontFamily: FONT, fontWeight: 600 }}>
                        Clear
                      </button>
                    </div>
                    {exercises.map(ex => {
                      const checked = !!sessionChecklist[ex.id]
                      return (
                        <label key={ex.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', border: `1.5px solid ${checked ? TEAL_MID : '#e2e8f0'}`, background: checked ? TEAL_LIGHT : 'white', cursor: 'pointer', transition: 'all .12s' }}>
                          <input type="checkbox" checked={checked} onChange={e => setSessionChecklist(prev => ({ ...prev, [ex.id]: e.target.checked }))}
                            style={{ width: 16, height: 16, accentColor: TEAL, cursor: 'pointer', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: checked ? TEAL_DARK : '#0f172a' }}>{ex.exercise_name}</span>
                            <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: 8 }}>{ex.sets} × {ex.reps}</span>
                            {ex.notes && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: 2 }}>{ex.notes}</div>}
                          </div>
                          {checked && <span style={{ fontSize: '13px', color: TEAL }}></span>}
                        </label>
                      )
                    })}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input placeholder="% of exercises completed (e.g. 85)" type="number" min="0" max="100" value={sessionForm.exercises_completed} onChange={e => setSessionForm({ ...sessionForm, exercises_completed: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                    <span style={{ fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>optional</span>
                  </div>
                )}
              </div>

              <div style={{ borderTop: '1.5px solid rgba(13,148,136,0.2)', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Session notes</label>
                  <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '3px', borderRadius: '8px' }}>
                    {(ff.soap_ai ? ['simple', 'soap'] : ['simple']).map(mode => (
                      <button key={mode} onClick={() => setNotesMode(mode)} style={{ padding: '5px 14px', borderRadius: '6px', border: 'none', background: notesMode === mode ? 'white' : 'transparent', color: notesMode === mode ? TEAL_DARK : '#64748b', cursor: 'pointer', fontSize: '12px', fontWeight: '700', fontFamily: FONT, boxShadow: notesMode === mode ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', transition: 'all .15s' }}>
                        {mode.toUpperCase()}
                      </button>
                    ))}
                    {!ff.soap_ai && (
                      <button title="Upgrade to unlock SOAP notes" style={{ padding: '5px 14px', borderRadius: '6px', border: 'none', background: 'transparent', color: '#cbd5e1', cursor: 'default', fontSize: '12px', fontWeight: '700', fontFamily: FONT }}>
                         SOAP
                      </button>
                    )}
                  </div>
                </div>
                {notesMode === 'simple' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      {(() => {
                        const lastNoteSession = [...sessions].sort((a, b) => a.session_number - b.session_number).reverse().find(s => s.notes)
                        if (!lastNoteSession) return <div />
                        return (
                          <button type="button"
                            onClick={() => setSessionForm(prev => ({ ...prev, notes: lastNoteSession.notes || '' }))}
                            title={`Copy notes from Session ${lastNoteSession.session_number}`}
                            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '7px', border: `1.5px solid ${TEAL_MID}`, background: TEAL_LIGHT, color: TEAL_DARK, cursor: 'pointer', fontSize: '11px', fontWeight: '700', fontFamily: FONT }}
                          >↑ Pull from S{lastNoteSession.session_number}</button>
                        )
                      })()}
                      <MicButton fieldName="notes" activeField={voiceField} onToggle={startVoice} supported={voiceSupported} />
                    </div>
                    <textarea
                      placeholder={voiceField === 'notes' ? ' Listening — speak now...' : "How did today's session go?"}
                      value={sessionForm.notes}
                      onChange={e => setSessionForm({ ...sessionForm, notes: e.target.value })}
                      style={{ ...inputStyle, height: '90px', resize: 'vertical', borderColor: voiceField === 'notes' ? '#fca5a5' : undefined }}
                    />
                  </div>
                )}
                {notesMode === 'soap' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {/* Pull-forward from last session */}
                      {(() => {
                        const sortedSessions = [...sessions].sort((a, b) => a.session_number - b.session_number)
                        const lastSoapSession = [...sortedSessions].reverse().find(s => s.soap_subjective || s.soap_objective || s.soap_assessment || s.soap_plan)
                        if (!lastSoapSession) return <div />
                        return (
                          <button
                            type="button"
                            onClick={() => setSessionForm(prev => ({
                              ...prev,
                              soap_subjective: lastSoapSession.soap_subjective || '',
                              soap_objective:  lastSoapSession.soap_objective  || '',
                              soap_assessment: lastSoapSession.soap_assessment || '',
                              soap_plan:       lastSoapSession.soap_plan       || '',
                            }))}
                            title={`Copy SOAP from Session ${lastSoapSession.session_number} and edit from there`}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '5px',
                              padding: '6px 12px', borderRadius: '8px',
                              border: `1.5px solid ${TEAL_MID}`, background: TEAL_LIGHT,
                              color: TEAL_DARK, cursor: 'pointer',
                              fontSize: '12px', fontWeight: '700', fontFamily: FONT,
                            }}
                          >
                            ↑ Pull from S{lastSoapSession.session_number}
                          </button>
                        )
                      })()}
                      <button
                        onClick={handleSoapDraft}
                        disabled={soapDrafting}
                        title="Let Caleb draft all four SOAP fields from your session notes"
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '6px 14px', borderRadius: '8px', border: 'none',
                          background: soapDrafting ? '#e2e8f0' : `linear-gradient(135deg, ${TEAL_DARK}, ${PURPLE_DARK})`,
                          color: soapDrafting ? '#94a3b8' : 'white',
                          cursor: soapDrafting ? 'not-allowed' : 'pointer',
                          fontSize: '12px', fontWeight: '700', fontFamily: FONT,
                          transition: 'all .15s',
                        }}
                      >
                        {soapDrafting ? 'Drafting…' : 'Draft with Caleb'}
                      </button>
                    </div>
                    {[
                      ['soap_subjective', 'S — Subjective', 'What the patient reports...'],
                      ['soap_objective',  'O — Objective',  'Measurable findings...'],
                      ['soap_assessment', 'A — Assessment', 'Clinical interpretation...'],
                      ['soap_plan',       'P — Plan',       'Next steps...'],
                    ].map(([field, label, ph]) => (
                      <div key={field}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <label style={{ ...labelStyle, color: TEAL, marginBottom: 0 }}>{label}</label>
                          <MicButton fieldName={field} activeField={voiceField} onToggle={startVoice} supported={voiceSupported} />
                        </div>
                        <textarea
                          placeholder={voiceField === field ? ' Listening — speak now...' : ph}
                          value={sessionForm[field]}
                          onChange={e => setSessionForm({ ...sessionForm, [field]: e.target.value })}
                          style={{ ...inputStyle, height: '70px', resize: 'vertical', borderColor: voiceField === field ? '#fca5a5' : undefined }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={handleLogSession} className="pd-btn" style={{ padding: '13px', background: `linear-gradient(135deg, ${TEAL_DARK}, ${PURPLE_DARK})`, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: '700', fontFamily: FONT, transition: 'all .15s' }}>
                Log Session
              </button>
            </div>
          </div>
        )}

        {activeTab === 'Referral' && (
          <div style={{ maxWidth: '540px' }}>
            {sectionLabel('Referring Physician')}

            {/* Doctor info form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
              <div>
                <label style={labelStyle}>Doctor name</label>
                <input
                  placeholder="e.g. Dr. Sarah Johnson"
                  value={referralForm.referring_doctor}
                  onChange={e => setReferralForm(f => ({ ...f, referring_doctor: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Practice / Hospital</label>
                <input
                  placeholder="e.g. Northside Orthopedics"
                  value={referralForm.referring_doctor_practice}
                  onChange={e => setReferralForm(f => ({ ...f, referring_doctor_practice: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Doctor email</label>
                <input
                  type="email"
                  placeholder="e.g. dr.johnson@northside.com"
                  value={referralForm.referring_doctor_email}
                  onChange={e => setReferralForm(f => ({ ...f, referring_doctor_email: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <button
                onClick={handleSaveReferralDoctor}
                disabled={referralSaving}
                style={{ padding: '11px', background: TEAL, color: 'white', border: 'none', borderRadius: '10px', cursor: referralSaving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: FONT, opacity: referralSaving ? 0.7 : 1 }}
              >
                {referralSaving ? 'Saving…' : 'Save Doctor Info'}
              </button>
            </div>

            {/* Communications */}
            <div style={{ marginTop: '28px', borderTop: '1.5px solid rgba(13,148,136,0.15)', paddingTop: '20px' }}>
              {sectionLabel('Communications')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '14px' }}>

                {/* Progress report */}
                <div style={{ background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Progress Report</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Send a treatment summary email to the referring doctor</div>
                    {p.last_doctor_report_sent && (
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                        Last sent: {new Date(p.last_doctor_report_sent).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleEmailDoctor}
                    disabled={doctorEmailSending || !referralForm.referring_doctor_email}
                    title={!referralForm.referring_doctor_email ? 'Add a doctor email above first' : ''}
                    style={{ flexShrink: 0, padding: '9px 16px', background: referralForm.referring_doctor_email ? TEAL : '#e2e8f0', color: referralForm.referring_doctor_email ? 'white' : '#94a3b8', border: 'none', borderRadius: '9px', cursor: referralForm.referring_doctor_email ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '700', fontFamily: FONT, whiteSpace: 'nowrap' }}
                  >
                    {doctorEmailSending ? 'Sending…' : 'Send Report'}
                  </button>
                </div>

                {/* Discharge thank-you letter */}
                <div style={{ background: p.is_discharged ? '#f0fdf4' : 'white', border: `1.5px solid ${p.is_discharged ? '#86efac' : '#e2e8f0'}`, borderRadius: '12px', padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Discharge Thank-You Letter</div>
                      {p.is_discharged && <span style={{ fontSize: '10px', fontWeight: '700', color: '#15803d', background: '#dcfce7', padding: '2px 7px', borderRadius: '99px' }}>PATIENT DISCHARGED</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Notify the referring doctor that the patient completed care</div>
                    {!p.is_discharged && <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '4px' }}>Discharge the patient first to send this letter</div>}
                  </div>
                  <button
                    onClick={handleThankYouLetter}
                    disabled={thankYouSending || !p.is_discharged || !referralForm.referring_doctor_email}
                    title={!p.is_discharged ? 'Discharge patient first' : !referralForm.referring_doctor_email ? 'Add doctor email above' : ''}
                    style={{ flexShrink: 0, padding: '9px 16px', background: (p.is_discharged && referralForm.referring_doctor_email) ? '#15803d' : '#e2e8f0', color: (p.is_discharged && referralForm.referring_doctor_email) ? 'white' : '#94a3b8', border: 'none', borderRadius: '9px', cursor: (p.is_discharged && referralForm.referring_doctor_email) ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '700', fontFamily: FONT, whiteSpace: 'nowrap' }}
                  >
                    {thankYouSending ? 'Sending…' : 'Send Letter'}
                  </button>
                </div>

              </div>
            </div>

            {/* Stats */}
            {sessions.length > 0 && (
              <div style={{ marginTop: '28px', borderTop: '1.5px solid rgba(13,148,136,0.15)', paddingTop: '20px' }}>
                {sectionLabel('Referral Summary')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '14px' }}>
                  {[
                    { label: 'Sessions', value: sessions.length },
                    { label: 'Pain at intake', value: (p.intake_pain_score != null ? p.intake_pain_score + '/10' : '—') },
                    { label: 'Pain now', value: (latest ? latest.pain_score + '/10' : '—') },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: TEAL_LIGHT, border: `1.5px solid ${TEAL_MID}`, borderRadius: '10px', padding: '12px 14px', textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: TEAL_DARK }}>{value}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', fontWeight: '600' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'Documents' && (() => {
          const DOC_CATEGORIES = ['Insurance Card', 'Referral', 'Imaging / X-Ray', 'Prescription', 'Prior Auth', 'Progress Report', 'Other']
          const FILE_ICONS = { pdf: '', jpg: '', jpeg: '', png: '', gif: '', webp: '', doc: '', docx: '', txt: '' }
          const getIcon = (name) => { const ext = name.split('.').pop().toLowerCase(); return FILE_ICONS[ext] || '' }
          const catColor = { 'Insurance Card': '#0ea5e9', 'Referral': '#8b5cf6', 'Imaging / X-Ray': '#f59e0b', 'Prescription': '#10b981', 'Prior Auth': '#f97316', 'Progress Report': TEAL, 'Other': '#64748b' }
          return (
            <div>
              <div
                onDragOver={e => { e.preventDefault(); setDocDragOver(true) }}
                onDragLeave={() => setDocDragOver(false)}
                onDrop={e => { e.preventDefault(); setDocDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleUploadFile(f) }}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${docDragOver ? TEAL : '#cbd5e1'}`,
                  borderRadius: '12px', padding: '32px', textAlign: 'center',
                  cursor: 'pointer', marginBottom: '20px',
                  background: docDragOver ? TEAL_LIGHT : '#f8fafc',
                  transition: 'all 0.15s',
                }}
              >
                <input ref={fileInputRef} type="file" style={{ display: 'none' }}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.txt"
                  onChange={e => { const f = e.target.files[0]; if (f) handleUploadFile(f); e.target.value = '' }}
                />
                <div style={{ fontSize: '28px', marginBottom: '8px' }}></div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                  {docUploading ? 'Uploading…' : 'Drop file here or click to upload'}
                </div>
                <div style={{ fontSize: '13px', color: '#94a3b8' }}>PDF, images, Word, TXT — up to 25 MB</div>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', flexShrink: 0 }}>Category:</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {DOC_CATEGORIES.map(c => (
                    <button key={c} onClick={() => setDocCategory(c)} style={{
                      padding: '5px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: '600',
                      cursor: 'pointer', fontFamily: FONT,
                      background: docCategory === c ? catColor[c] || TEAL : '#f1f5f9',
                      color: docCategory === c ? 'white' : '#64748b',
                      border: `1.5px solid ${docCategory === c ? catColor[c] || TEAL : '#e2e8f0'}`,
                      transition: 'all 0.15s',
                    }}>{c}</button>
                  ))}
                </div>
              </div>

              {documents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '15px', background: 'rgba(248,250,252,0.78)', borderRadius: '12px', border: '1.5px dashed #e2e8f0' }}>
                  No documents uploaded yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {documents.map(doc => (
                    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', background: 'rgba(255,255,255,0.78)', border: '1.5px solid rgba(124,58,237,0.25)', borderRadius: '10px', padding: '12px 16px' }}>
                      <span style={{ fontSize: '22px', flexShrink: 0 }}>{getIcon(doc.file_name)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.file_name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '99px', background: (catColor[doc.category] || '#64748b') + '18', color: catColor[doc.category] || '#64748b', border: `1px solid ${(catColor[doc.category] || '#64748b')}33` }}>{doc.category}</span>
                          {doc.size_label && <span style={{ fontSize: '12px', color: '#94a3b8' }}>{doc.size_label}</span>}
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        {doc.url && (
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 12px', background: TEAL_LIGHT, color: TEAL, border: `1px solid ${TEAL_MID}`, borderRadius: '6px', fontSize: '13px', fontWeight: '600', textDecoration: 'none', fontFamily: FONT }}>View</a>
                        )}
                        <button onClick={() => handleDeleteDoc(doc.id, doc.file_name)} style={{ padding: '6px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: FONT }}></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })()}

        {activeTab === 'Messages' && (
          <div>
            {sectionLabel('Messages with ' + patient.name)}
            <div style={{ marginTop: '16px' }}>
              <Messages patientId={patient.id} senderRole="therapist" senderName="Therapist" />
            </div>
          </div>
        )}

        {activeTab === 'Intake' && (
          <div>
            {/*  Send / Print card  */}
            <div style={{ background: TEAL_LIGHT, border: `1.5px solid ${TEAL_MID}`, borderRadius: '12px', padding: '18px', marginBottom: '16px' }}>
              {sectionLabel('Send intake form')}
              <p style={{ fontSize: '14px', color: '#64748b', margin: '8px 0 14px', lineHeight: '1.6' }}>
                Email a secure link to {patient.name}, or print a blank paper form for walk-in patients.
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={handleSendIntake} disabled={intakeSending} className="pd-btn" style={{ padding: '10px 22px', background: TEAL, color: 'white', border: 'none', borderRadius: '8px', cursor: intakeSending ? 'default' : 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: FONT, transition: 'all .15s', opacity: intakeSending ? 0.7 : 1 }}>
                  {intakeSending ? 'Generating...' : ' Email Intake Form'}
                </button>
                <button onClick={handlePrintIntake} className="pd-btn" style={{ padding: '10px 22px', background: 'white', color: TEAL_DARK, border: `1.5px solid ${TEAL_MID}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: FONT, transition: 'all .15s' }}>
                   Print Paper Form
                </button>
              </div>
              {intakeUrl && (
                <div style={{ marginTop: '14px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>Or copy link manually:</div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input readOnly value={intakeUrl} onClick={e => e.target.select()} style={{ ...inputStyle, flex: 1, fontSize: '12px', color: '#64748b', background: 'white' }} />
                    <button onClick={() => { navigator.clipboard.writeText(intakeUrl); alert('Copied!') }} style={{ padding: '10px 16px', border: '1.5px solid rgba(124,58,237,0.25)', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '13px', fontFamily: FONT, fontWeight: '600' }}>Copy</button>
                  </div>
                </div>
              )}
            </div>

            {/*  Upload paper scan card  */}
            <div style={{ background: 'rgba(255,255,255,0.78)', border: '1.5px solid rgba(124,58,237,0.25)', borderRadius: '12px', padding: '18px', marginBottom: '28px' }}>
              {sectionLabel('Upload completed paper form')}
              <p style={{ fontSize: '14px', color: '#64748b', margin: '8px 0 14px', lineHeight: '1.6' }}>
                If the patient filled out a paper form, scan or photograph it and upload here. PDF, PNG, and JPG accepted (max 20 MB).
              </p>
              <input ref={scanInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleScanUpload} style={{ display: 'none' }} />
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => scanInputRef.current?.click()} disabled={scanUploading} style={{ padding: '10px 20px', background: scanUploading ? '#f1f5f9' : PURPLE, color: scanUploading ? '#94a3b8' : 'white', border: 'none', borderRadius: '8px', cursor: scanUploading ? 'default' : 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: FONT, transition: 'all .15s' }}>
                  {scanUploading ? 'Uploading…' : ' Upload Scan'}
                </button>
                {scanFilename && (
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Uploaded {scanUploadedAt ? new Date(scanUploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    {' · '}
                    <a href={API + '/patients/' + patient.id + '/intake-scan'} target="_blank" rel="noreferrer" style={{ color: TEAL, fontWeight: '600' }}>View / Download ↗</a>
                  </span>
                )}
              </div>

              {/* Inline preview */}
              {scanFilename && (() => {
                const isPdf = scanFilename.toLowerCase().endsWith('.pdf')
                const src = API + '/patients/' + patient.id + '/intake-scan'
                return (
                  <div style={{ marginTop: '16px', border: '1.5px solid rgba(124,58,237,0.25)', borderRadius: '10px', overflow: 'hidden', background: '#f8fafc' }}>
                    <div style={{ padding: '8px 14px', background: '#f1f5f9', borderBottom: '1px solid rgba(124,58,237,0.18)', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span> Scanned Intake Form</span>
                      <a href={src} target="_blank" rel="noreferrer" style={{ color: TEAL, fontWeight: '600', fontSize: '11px', textDecoration: 'none' }}>Open full size ↗</a>
                    </div>
                    {isPdf
                      ? <iframe src={src} title="Intake scan" style={{ width: '100%', height: '600px', border: 'none', display: 'block' }} />
                      : <img src={src} alt="Intake scan" style={{ width: '100%', maxHeight: '600px', objectFit: 'contain', display: 'block' }} />
                    }
                  </div>
                )
              })()}
            </div>

            {!p.intake_completed ? (
              <div style={{ textAlign: 'center', padding: '48px', background: 'rgba(248,250,252,0.78)', borderRadius: '12px', border: '1.5px dashed #e2e8f0' }}>
                <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: '16px', color: '#94a3b8' }}>No intake form submitted yet.</div>
              </div>
            ) : (() => {
              const parseList = (val) => { try { const a = JSON.parse(val); return Array.isArray(a) ? a.join(', ') : val } catch { return val } }
              const painBar = (score) => score != null ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: '700', fontSize: '15px', color: score <= 3 ? '#16a34a' : score <= 6 ? '#d97706' : '#dc2626' }}>{score}/10</span>
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>{['No pain','Very mild','Mild','Noticeable','Moderate','Moderate','Severe','Severe','Intense','Excruciating','Unbearable'][Math.round(score)]}</span>
                </span>
              ) : null
              const sections = [
                { title: 'Personal Information', items: [
                  ['Date of Birth', p.dob], ['Phone', p.phone], ['Address', p.address],
                  ['Emergency Contact', p.emergency_contact_name], ['Emergency Contact Phone', p.emergency_contact_phone],
                  ['Referring Physician', p.referring_physician || p.referring_doctor],
                  ['Referring Doctor Email', p.referring_doctor_email
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>{p.referring_doctor_email}</span>
                        <button onClick={handleEmailDoctor} disabled={doctorEmailSending} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: 'none', background: TEAL_DARK, color: 'white', cursor: 'pointer', fontFamily: FONT, fontWeight: '700' }}>
                          {doctorEmailSending ? 'Sending…' : 'Send Report'}
                        </button>
                      </span>
                    : <button onClick={() => {
                        const email = prompt('Enter referring doctor email address:')
                        if (!email) return
                        axios.patch(API + '/patients/' + patient.id + '/referring-doctor', { referring_doctor_email: email })
                          .then(() => { fetchAll(); onToast && onToast('Doctor email saved.') })
                          .catch(() => alert('Failed to save email.'))
                      }} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: '1.5px dashed #cbd5e1', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontFamily: FONT }}>
                        + Add email
                      </button>
                  ],
                ]},
                { title: 'Insurance & Billing', items: [
                  ['Insurance Provider', p.insurance_provider], ['Policy Number', p.insurance_policy_number],
                  ['Group Number', p.insurance_group_number], ['Policy Holder', p.policy_holder_name],
                  ["Workers' Comp", p.workers_comp ? 'Yes' : null], ['MVA Related', p.mva_related ? 'Yes' : null],
                ]},
                { title: 'Pain Summary', items: [
                  ['Current Pain', p.pain_current != null ? painBar(p.pain_current) : null],
                  ['Worst Pain (past week)', p.pain_worst != null ? painBar(p.pain_worst) : null],
                  ['Best Pain (past week)', p.pain_best != null ? painBar(p.pain_best) : null],
                  ['Pain Type', p.pain_type],
                  ['Affected Regions', p.pain_body_regions ? parseList(p.pain_body_regions) : null],
                ]},
                { title: 'Injury Details', items: [
                  ['Mechanism of Injury', p.mechanism_of_injury], ['Symptom Onset', p.symptom_onset],
                  ['Aggravating Factors', p.aggravating_factors], ['Relieving Factors', p.relieving_factors],
                  ['Functional Limitations', p.functional_limitations],
                ]},
                { title: 'Medical History', items: [
                  ['Primary Care Physician', p.primary_care_physician], ['Medications', p.medications],
                  ['Allergies', p.allergies], ['Prior Surgeries', p.prior_surgeries],
                  ['Prior PT', p.prior_pt ? 'Yes' : 'No'], ['Prior PT Notes', p.prior_pt_notes],
                  ['Conditions / Comorbidities', p.comorbidities ? parseList(p.comorbidities) : null],
                  ['Work Status', p.work_status],
                ]},
                { title: 'Lifestyle & Goals', items: [
                  ['Occupation', p.occupation], ['Sports / Activities', p.sport_activities],
                  ['Activity Level', p.activity_level], ['Patient Goals', p.patient_goals],
                ]},
                { title: 'Consent', items: [
                  ['Consent Signed', p.consent_signed ? ' Yes' : 'No'],
                  ['Initials', p.consent_initials],
                  ['Signed At', p.consent_signed_at ? new Date(p.consent_signed_at).toLocaleString() : null],
                ]},
              ]
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {sections.map(sec => {
                    const filled = sec.items.filter(([, v]) => v)
                    if (filled.length === 0) return null
                    return (
                      <div key={sec.title} style={{ border: '1.5px solid rgba(124,58,237,0.25)', borderRadius: '12px', overflow: 'hidden' }}>
                        <div style={{ background: TEAL_LIGHT, padding: '10px 18px', fontSize: '11px', fontWeight: '700', color: TEAL_DARK, textTransform: 'uppercase', letterSpacing: '0.09em', borderBottom: `1px solid ${TEAL_MID}` }}>{sec.title}</div>
                        <div style={{ padding: '4px 0' }}>
                          {filled.map(([label, value]) => (
                            <div key={label} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '12px', padding: '10px 18px', borderBottom: '1px solid #f8fafc' }}>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: '2px' }}>{label}</div>
                              <div style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.6' }}>{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        )}

        {/* ── BILLING ──────────────────────────────────────────────────── */}
        {activeTab === 'Billing' && (
          <TherapistBillingPanel patient={patient} clinicId={patient.clinic_id || currentUser?.clinic_id} currentUser={currentUser} />
        )}
      </div>
    </div>
  )
}

// ─── Therapist Billing Panel ──────────────────────────────────────────────────
function TherapistBillingPanel({ patient, clinicId, currentUser }) {
  const [invoices,        setInvoices]        = useState([])
  const [loading,         setLoading]         = useState(true)
  const [submitting,      setSubmitting]       = useState(false)
  const [markingPaid,     setMarkingPaid]      = useState(null)   // invoice id
  const [cancelling,      setCancelling]       = useState(null)   // invoice id
  const [formOpen,        setFormOpen]         = useState(false)
  const [form, setForm] = useState({ amount: '', description: '', due_date: '' })
  const [formError,       setFormError]        = useState('')

  useEffect(() => { fetchInvoices() }, [patient.id])

  const fetchInvoices = () => {
    setLoading(true)
    axios.get(`${API}/billing/patient/${patient.id}/invoices`)
      .then(r => setInvoices(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  const handleCreate = async () => {
    const amt = parseFloat(form.amount)
    if (!amt || amt <= 0) { setFormError('Please enter a valid amount.'); return }

    const resolvedClinicId = clinicId || currentUser?.clinic_id || patient?.clinic_id
    if (!resolvedClinicId) {
      setFormError('Unable to determine clinic. Please refresh and try again.')
      return
    }

    setSubmitting(true); setFormError('')
    try {
      await axios.post(`${API}/billing/patient/create-invoice`, {
        clinic_id: resolvedClinicId,
        patient_id: patient.id,
        amount_dollars: amt,
        description: form.description || undefined,
        due_date: form.due_date || undefined,
        created_by_user_id: currentUser?.id,
      })
      setForm({ amount: '', description: '', due_date: '' })
      setFormOpen(false)
      fetchInvoices()
    } catch (err) {
      // Pydantic validation errors return detail as an array; stringify it clearly
      const detail = err.response?.data?.detail
      if (Array.isArray(detail)) {
        setFormError(detail.map(d => d.msg || JSON.stringify(d)).join('; '))
      } else {
        setFormError(typeof detail === 'string' ? detail : 'Failed to create invoice. Check your connection and try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleMarkPaid = async (inv) => {
    if (!window.confirm(`Mark invoice #${String(inv.id).padStart(4,'0')} ($${(inv.amount_cents/100).toFixed(2)}) as paid?`)) return
    setMarkingPaid(inv.id)
    try {
      await axios.post(`${API}/billing/patient/mark-paid`, { invoice_id: inv.id })
      fetchInvoices()
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to mark invoice paid.')
    } finally {
      setMarkingPaid(null)
    }
  }

  const handleCancel = async (inv) => {
    if (!window.confirm(`Cancel invoice #${String(inv.id).padStart(4,'0')}? This cannot be undone.`)) return
    setCancelling(inv.id)
    try {
      await axios.post(`${API}/billing/patient/cancel-invoice`, { invoice_id: inv.id })
      fetchInvoices()
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel invoice.')
    } finally {
      setCancelling(null)
    }
  }

  // Summaries
  const paid       = invoices.filter(i => i.status === 'paid')
  const unpaid     = invoices.filter(i => i.status === 'pending' || i.status === 'overdue')
  const totalPaid  = paid.reduce((s, i) => s + i.amount_cents, 0) / 100
  const totalOwed  = unpaid.reduce((s, i) => s + i.amount_cents, 0) / 100

  const statusColor = (s) => s === 'paid' ? { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' }
    : s === 'pending' ? { bg: '#fffbeb', border: '#fde68a', text: '#d97706' }
    : s === 'overdue' ? { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' }
    : { bg: '#f8fafc', border: '#e2e8f0', text: '#94a3b8' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 780 }}>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 16, padding: '22px 24px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Revenue Received</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#15803d', fontFamily: DISPLAY }}>${totalPaid.toFixed(2)}</div>
          <div style={{ fontSize: 12, color: '#86efac', marginTop: 4 }}>{paid.length} invoice{paid.length !== 1 ? 's' : ''} paid</div>
        </div>
        <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 16, padding: '22px 24px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Outstanding</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#b45309', fontFamily: DISPLAY }}>${totalOwed.toFixed(2)}</div>
          <div style={{ fontSize: 12, color: '#fcd34d', marginTop: 4 }}>{unpaid.length} invoice{unpaid.length !== 1 ? 's' : ''} pending</div>
        </div>
      </div>

      {/* Create invoice button */}
      <div>
        <button
          onClick={() => setFormOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: TEAL, color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: FONT, transition: 'all .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = TEAL_DARK}
          onMouseLeave={e => e.currentTarget.style.background = TEAL}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
          {formOpen ? 'Cancel' : 'Create Invoice'}
        </button>

        {formOpen && (
          <div style={{ marginTop: 16, background: 'white', border: '1.5px solid rgba(13,148,136,0.25)', borderRadius: 16, padding: '24px' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 18 }}>New Invoice for {patient.name}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Amount ($)</label>
                <input
                  type="number" min="0.01" step="0.01" placeholder="150.00"
                  value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  style={{ ...inputStyle, fontWeight: 700, fontSize: 16 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Description</label>
                <input
                  placeholder="e.g. PT session – Week 3"
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Due Date</label>
                <input
                  type="date"
                  value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                  style={inputStyle}
                />
              </div>
            </div>
            {formError && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontSize: 13, marginBottom: 12 }}>
                {formError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleCreate} disabled={submitting}
                style={{ padding: '11px 28px', background: TEAL, color: 'white', border: 'none', borderRadius: 9, cursor: submitting ? 'wait' : 'pointer', fontSize: 14, fontWeight: 700, fontFamily: FONT, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Sending…' : 'Send Invoice to Patient'}
              </button>
              <button onClick={() => setFormOpen(false)}
                style={{ padding: '11px 20px', background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: FONT, color: '#64748b' }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice list */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Invoice History</div>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading…</div>
        ) : invoices.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', background: '#f8fafc', borderRadius: 16, border: '1.5px dashed #e2e8f0' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🧾</div>
            <div style={{ fontSize: 15, color: '#94a3b8', fontStyle: 'italic' }}>No invoices yet. Create one above to get started.</div>
          </div>
        ) : (
          <div style={{ background: 'white', border: '1.5px solid rgba(13,148,136,0.2)', borderRadius: 16, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 90px 100px 90px 160px', gap: 10, padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: FONT }}>
              <span>Invoice</span>
              <span>Description</span>
              <span>Amount</span>
              <span>Due</span>
              <span>Status</span>
              <span></span>
            </div>
            {invoices.map((inv, i) => {
              const isOverdue = inv.status === 'pending' && inv.due_date && new Date(inv.due_date) < new Date()
              const displayStatus = isOverdue ? 'overdue' : inv.status
              const sc = statusColor(displayStatus)
              return (
                <div key={inv.id} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 90px 100px 90px 160px', gap: 10, padding: '16px 20px', borderBottom: i < invoices.length - 1 ? '1px solid #f1f5f9' : 'none', alignItems: 'center', fontFamily: FONT, background: displayStatus === 'overdue' ? 'rgba(220,38,38,0.02)' : 'white' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
                    #{String(inv.id).padStart(4,'0')}
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}</div>
                  </div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.4 }}>{inv.description || '—'}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>${(inv.amount_cents / 100).toFixed(2)}</div>
                  <div style={{ fontSize: 12, color: displayStatus === 'overdue' ? '#dc2626' : '#64748b' }}>{inv.due_date || '—'}</div>
                  <div>
                    <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text }}>
                      {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(displayStatus === 'pending' || displayStatus === 'overdue') && (
                      <button onClick={() => handleMarkPaid(inv)} disabled={markingPaid === inv.id}
                        style={{ padding: '6px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: 7, cursor: markingPaid === inv.id ? 'wait' : 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT, opacity: markingPaid === inv.id ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                        {markingPaid === inv.id ? '…' : '✓ Mark Paid'}
                      </button>
                    )}
                    {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                      <button onClick={() => handleCancel(inv)} disabled={cancelling === inv.id}
                        style={{ padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 7, cursor: cancelling === inv.id ? 'wait' : 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT, opacity: cancelling === inv.id ? 0.7 : 1 }}>
                        {cancelling === inv.id ? '…' : '✕'}
                      </button>
                    )}
                    {inv.status === 'paid' && inv.paid_at && (
                      <span style={{ fontSize: 11, color: '#86efac' }}>Paid {new Date(inv.paid_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default PatientDashboard